import React, { useState } from "react";
import { observer } from "mobx-react";
import get from "lodash/get";
import { accountsState } from "../AccountsState";
import PaginationComponent from "Components/common/PaginationComponent";
import UpdateContactStageDropdown from "Components/ContactCard/UpdateContactStageDropdown";
import CustomTooltip from "Components/common/CustomTooltip";
import EmailInsightsComponent from "Components/common/EmailInsights";
import Utils from "Utils/utils";
import { ContactUtils } from "Utils/ContactUtils";
import CheckBox from "Components/common/CheckBox";

const ContactsDataTable = observer(
  ({
    loader = false,
    tableData = {},
    setTableData = () => {},
    paginate = () => {},
    reloadContactsPage = () => {},
    openContactCard = () => {},
    allAccountsSelectedCheckbox = false,
    setAllAccountsSelectedCheckbox = () => {},
    setAllAccountsSelectedInView = () => {},
    contactFields = []
  }) => {
    const { contacts = [], totalPages = 0, currentPage = 0 } = tableData || {};
    const [showTooltip, setShowTooltip] = useState({});

    const handleContactSlider = (event = "", contact = {}) => {
      Utils.preventDefaultFn(event);
      openContactCard(contact);
    };

    const handleAllCheckboxAction = (value = false, type = "") => {
      if (!loader) {
        setAllAccountsSelectedCheckbox(value);
        if (!value) {
          setAllAccountsSelectedInView(false);
        }
        setTableData({
          ...tableData,
          contacts: [...tableData?.contacts].map(item => ({
            ...item,
            isChecked: value
          }))
        });
      }
    };

    const handleCheckboxAction = (value = false, type = "") => {
      let tempTableArr = [];
      let count = 0;
      if (tableData?.contacts?.length > 0) {
        tempTableArr = tableData?.contacts.map(item => {
          const tempItem =
            item?.id === type ? { ...item, isChecked: value } : item;
          tempItem.isChecked && count++;
          return tempItem;
        });
      }
      setAllAccountsSelectedCheckbox(tableData?.contacts?.length === count);
      setTableData({
        ...tableData,
        contacts: tempTableArr
      });
    };

    const checkboxObj = {
      id: "all",
      key: "all",
      checked: allAccountsSelectedCheckbox,
      cbk: handleAllCheckboxAction
    };

    const enabledFields = contactFields.filter(f => f.enabled);

    const renderField = (field, item, index) => {
      const {
        id = "",
        name = "",
        firstName = "",
        lastName = "",
        email = "",
        jobTitle = "",
        company = "",
        website = "",
        stage = "",
        owner = {},
        lastContacted = "",
        stageDisplayName = "",
        insights = {},
        contactTags = []
      } = item || {};

      const fieldKey = field.key;

      switch (fieldKey) {
        case "name": {
          const fullName = Utils.getContactFullName(firstName, lastName, name);
          return (
            <td key={fieldKey} className="nameWrapper">
              {fullName && (
                <p
                  title={fullName}
                  className="name triggerContactCard underlineHighlight"
                >
                  {fullName}
                </p>
              )}
            </td>
          );
        }
        case "email":
          return (
            <td key={fieldKey}>
              <span className="email triggerContactCard underlineHighlight">
                {email || "--"}
              </span>
            </td>
          );
        case "jobTitle":
          return (
            <td key={fieldKey} className="companyJobTile">
              <p title={jobTitle} className="job">
                {jobTitle || "--"}
              </p>
            </td>
          );
        case "stageDisplayName":
          return (
            <td
              key={fieldKey}
              className="status"
              onClick={e => e.stopPropagation()}
            >
              <UpdateContactStageDropdown
                stage={stageDisplayName || stage || "--"}
                contactStages={accountsState?.contactsStages}
                reloadCbk={reloadContactsPage}
                pageType="contacts"
                entityId={id}
              />
            </td>
          );
        case "company":
          return (
            <td key={fieldKey} className="companyJobTile">
              <p title={company} className="company">
                {company || "--"}
              </p>
            </td>
          );
        case "website":
          return (
            <td key={fieldKey}>
              {website ? (
                <p className="website-wrapper" title={website}>
                  <a
                    href={
                      website.startsWith("http")
                        ? website
                        : `https://${website}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                  >
                    {website}
                  </a>
                </p>
              ) : (
                "--"
              )}
            </td>
          );
        case "insights":
          return (
            <td key={fieldKey} className="stageInsight">
              {insights &&
              typeof insights === "object" &&
              Object.keys(insights).length > 0 ? (
                <EmailInsightsComponent emailInsight={insights} />
              ) : (
                "--"
              )}
            </td>
          );
        case "contactTags":
          return (
            <td key={fieldKey} className="tag">
              {ContactUtils.getTagNameDom(contactTags)}
            </td>
          );
        case "lastContacted":
          return (
            <td key={fieldKey} className="lastContacted">
              {Utils.formatDate(lastContacted) || "--"}
            </td>
          );
        case "linkedinId": {
          const linkedinUrl = get(item, fieldKey);
          return (
            <td key={fieldKey} className={field.headerClassName || ""} onClick={e => e.stopPropagation()}>
              {linkedinUrl ? (
                <p className="linkedin-wrapper" title={linkedinUrl}>
                  <a
                    href={linkedinUrl.startsWith("http") ? linkedinUrl : `https://${linkedinUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="linkedin-link"
                  >
                    {linkedinUrl}
                  </a>
                </p>
              ) : (
                "--"
              )}
            </td>
          );
        }
        case "owner":
          return (
            <td
              key={fieldKey}
              className="ctOwner"
              onClick={e => e.stopPropagation()}
              onMouseEnter={() => setShowTooltip({ index, value: true })}
              onMouseLeave={() => setShowTooltip({ index, value: false })}
            >
              {owner && Object.keys(owner)?.length ? (
                <>
                  <span className="circleOwner">
                    {Utils.getOwnerInitial(owner)}
                  </span>
                  {showTooltip?.index === index &&
                    showTooltip?.value && (
                      <CustomTooltip
                        text={`Added by ${Utils.getOwnerName(owner)}`}
                      />
                    )}
                </>
              ) : (
                "--"
              )}
            </td>
          );
        default: {
          const value = get(item, fieldKey) || "--";
          return (
            <td key={fieldKey} className={field.headerClassName || ""}>
              <p title={typeof value === "string" ? value : undefined}>
                {value}
              </p>
            </td>
          );
        }
      }
    };

    return (
      <div
        className={`dataTableCont ${
          totalPages > 1 || loader ? "paginationSection" : ""
        }`}
      >
        {!loader ? (
          <>
            <div
              className="tableContainer"
              style={{
                overflow:
                  !loader && (!contacts || contacts?.length < 1)
                    ? "hidden"
                    : "auto"
              }}
            >
              <table>
                <thead>
                  <tr className="ctTableHeader">
                    <th className="ctSelectAll">
                      {contacts?.length > 0 && (
                        <CheckBox {...checkboxObj} />
                      )}
                    </th>
                    {enabledFields.map(field => (
                      <th
                        key={field.key}
                        className={field.headerClassName || ""}
                      >
                        <div>{field.displayName}</div>
                      </th>
                    ))}
                    <th>
                      <div>Actions</div>
                    </th>
                  </tr>
                </thead>
                {contacts?.length > 0 && (
                  <tbody>
                    {[...contacts].map((item, index) => {
                      const { id = "", isChecked = false, owner = {} } = item || {};
                      return (
                        <tr
                          key={`${index}${id}ContactRowBody`}
                          className="ctTableBody"
                          style={{ cursor: "pointer" }}
                          onClick={() => openContactCard(item)}
                        >
                          <td
                            className="ctSelectAll"
                            onClick={e => e.stopPropagation()}
                          >
                            <CheckBox
                              key={id}
                              id={id}
                              checked={isChecked || false}
                              cbk={handleCheckboxAction}
                            />
                          </td>
                          {enabledFields.map(field =>
                            renderField(field, item, index)
                          )}
                          <td
                            className="actionDp moreOption"
                            onClick={e => e.stopPropagation()}
                          >
                            <i
                              className="material-icons-outlined icon-hover-btn"
                              title="More Options"
                              onClick={() => openContactCard(item)}
                            >
                              more_vert
                            </i>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                )}
              </table>
              {!loader && (!contacts || contacts?.length < 1) && (
                <EmptyContacts />
              )}
            </div>
          </>
        ) : (
          <TableLoader count={10} fields={enabledFields} />
        )}
        <PaginationComponent
          callback={paginate}
          loading={loader}
          totalPages={totalPages}
          currentPage={currentPage || 1}
        />
      </div>
    );
  }
);

const TableLoader = ({ count = 0, fields = [] }) => {
  return (
    <div className="tableContainer accountContactsLoader">
      <table>
        <thead>
          <tr className="ctTableHeader">
            <th className="ctSelectAll" />
            {fields.map((item, index) => (
              <th key={`${item.key}${index}Loader`} className={item.headerClassName || ""} />
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: count }, (_, index) => (
            <tr key={`${index}ContactRowBodyLoader`} className="ctTableBody">
              <td className="ctSelectAll">
                <span className="linear-background" />
              </td>
              {fields.map((ele, cIdx) => (
                <td
                  key={`${ele.key}${index}${cIdx}RowDataLoader`}
                  className={ele.headerClassName || ""}
                >
                  <span className="linear-background" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const EmptyContacts = () => {
  return (
    <div className="emptyContactsSection">
      <i className="material-icons-outlined">supervisor_account</i>
      <div className="text">No Contacts Found</div>
    </div>
  );
};

export default ContactsDataTable;
